import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, peso, BACKEND, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BrandLogo } from "@/components/BrandLogo";
import { STATUS_LABELS, STATUS_COLORS, fmtAddress } from "@/components/OrderTicket";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";
import {
  LayoutDashboard, ShoppingBag, Package, Building2, Truck, Users, UserCog,
  FileBarChart, ScrollText, Activity, LogOut, Plus, Download, Upload, Search, Menu,
} from "lucide-react";
import { toast } from "sonner";

const NAV = [
  { key: "overview", label: "Live Dashboard", icon: LayoutDashboard },
  { key: "orders", label: "Orders", icon: ShoppingBag },
  { key: "products", label: "Products", icon: Package },
  { key: "branches", label: "Branches", icon: Building2 },
  { key: "delivery", label: "Delivery & Payments", icon: Truck },
  { key: "customers", label: "Customers (CRM)", icon: Users },
  { key: "staff", label: "Staff & Roles", icon: UserCog },
  { key: "reports", label: "Reports", icon: FileBarChart },
  { key: "audit", label: "Audit Trail", icon: ScrollText },
  { key: "health", label: "System Health", icon: Activity },
];

function Stat({ label, value, accent }) {
  return (
    <div className="bg-white rounded-2xl border p-4" data-testid={`kpi-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-display text-2xl font-extrabold mt-1" style={{ color: accent || "#1A251C" }}>{value}</div>
    </div>
  );
}

/* ---------------- Overview ---------------- */
function Overview() {
  const [k, setK] = useState(null);
  useEffect(() => { const l = () => api.get("/admin/kpis").then((r) => setK(r.data)); l(); const t = setInterval(l, 8000); return () => clearInterval(t); }, []);
  if (!k) return <p className="text-muted-foreground">Loading KPIs…</p>;
  const catData = [
    { name: "Food", v: k.food_sales }, { name: "Drinks", v: k.drink_sales }, { name: "Essentials", v: k.essentials_sales },
  ];
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Today's Sales" value={peso(k.today_sales)} accent="#1B5E20" />
        <Stat label="Today's Orders" value={k.today_orders} accent="#0284C7" />
        <Stat label="Avg Order Value" value={peso(k.avg_order_value)} accent="#B78103" />
        <Stat label="Delivery Fees" value={peso(k.delivery_fees)} accent="#8B5CF6" />
        <Stat label="Delivery Orders" value={k.delivery_orders} />
        <Stat label="Pick-Up Orders" value={k.pickup_orders} />
        <Stat label="Completed" value={k.completed_orders} accent="#16A34A" />
        <Stat label="Cancelled" value={k.cancelled_orders} accent="#EF4444" />
        <Stat label="Active Customers" value={k.active_customers} />
        <Stat label="New Customers" value={k.new_customers} accent="#1B5E20" />
        <Stat label="Food Sales" value={peso(k.food_sales)} />
        <Stat label="Essentials Sales" value={peso(k.essentials_sales)} />
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border p-5">
          <h3 className="font-semibold mb-3">Sales by Type (Today)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={catData}><XAxis dataKey="name" /><YAxis /><Tooltip formatter={(v) => peso(v)} /><Bar dataKey="v" radius={[6, 6, 0, 0]}>{catData.map((_, i) => <Cell key={i} fill={["#1B5E20", "#C62828", "#FFB300"][i]} />)}</Bar></BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-2xl border p-5">
          <h3 className="font-semibold mb-3">Best Sellers (Today)</h3>
          {k.best_sellers.length === 0 ? <p className="text-sm text-muted-foreground">No sales yet today.</p> :
            k.best_sellers.map((b, i) => <div key={i} className="flex justify-between py-1.5 border-b last:border-0 text-sm"><span>{i + 1}. {b.name}</span><Badge variant="secondary">{b.qty} sold</Badge></div>)}
          <h3 className="font-semibold mt-4 mb-2">Low Stock</h3>
          {k.low_stock.length === 0 ? <p className="text-sm text-muted-foreground">All items in stock.</p> :
            k.low_stock.map((p, i) => <div key={i} className="flex justify-between py-1 text-sm"><span>{p.name}</span><Badge className="bg-[#FFB300] text-[#1A251C]">{p.status}</Badge></div>)}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Orders ---------------- */
function Orders() {
  const [orders, setOrders] = useState([]);
  useEffect(() => { const l = () => api.get("/orders").then((r) => setOrders(r.data)); l(); const t = setInterval(l, 8000); return () => clearInterval(t); }, []);
  return (
    <div className="bg-white rounded-2xl border overflow-x-auto">
      <Table>
        <TableHeader><TableRow><TableHead>Order #</TableHead><TableHead>Customer</TableHead><TableHead>Type</TableHead><TableHead>Total</TableHead><TableHead>Payment</TableHead><TableHead>Status</TableHead><TableHead>Time</TableHead></TableRow></TableHeader>
        <TableBody>
          {orders.map((o) => (
            <TableRow key={o.id} data-testid={`admin-order-${o.order_number}`}>
              <TableCell className="font-semibold text-[#1B5E20]">{o.order_number}</TableCell>
              <TableCell>{o.contact_name}</TableCell>
              <TableCell className="capitalize">{o.order_type}</TableCell>
              <TableCell>{peso(o.grand_total)}</TableCell>
              <TableCell className="uppercase text-xs">{o.payment_method}</TableCell>
              <TableCell><Badge style={{ background: STATUS_COLORS[o.status] }} className="text-white text-[10px]">{STATUS_LABELS[o.status]}</Badge></TableCell>
              <TableCell className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleString()}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {orders.length === 0 && <p className="text-center py-10 text-muted-foreground">No orders yet.</p>}
    </div>
  );
}

/* ---------------- Products ---------------- */
const EMPTY_P = { name: "", price: "", category: "Family Meals", description: "", image_url: "", stock_status: "available", available: true, featured: false };
function Products() {
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const load = useCallback(() => api.get("/products/all").then((r) => setProducts(r.data)), []);
  useEffect(() => { load(); api.get("/categories").then((r) => setCats(r.data)); }, [load]);

  const openNew = () => { setEditing({ ...EMPTY_P }); setOpen(true); };
  const openEdit = (p) => { setEditing({ ...p }); setOpen(true); };

  const upload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing((p) => ({ ...p, image_url: `${BACKEND}${data.url}` }));
      toast.success("Image uploaded.");
    } catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const save = async () => {
    try {
      const body = { ...editing, price: Number(editing.price) };
      if (editing.id) await api.put(`/products/${editing.id}`, body);
      else await api.post("/products", body);
      toast.success("Product saved."); setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const archive = async (p) => { await api.post(`/products/${p.id}/archive`); toast.success("Archived."); load(); };

  return (
    <div>
      <div className="flex justify-between mb-4"><h3 className="font-semibold">Product Catalog</h3><Button className="bg-[#1B5E20]" onClick={openNew} data-testid="add-product-btn"><Plus size={16} className="mr-1" /> Add Product</Button></div>
      <div className="bg-white rounded-2xl border overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Product</TableHead><TableHead>SKU</TableHead><TableHead>Category</TableHead><TableHead>Price</TableHead><TableHead>Stock</TableHead><TableHead>Available</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {products.map((p) => (
              <TableRow key={p.id} data-testid={`product-row-${p.id}`}>
                <TableCell className="flex items-center gap-2">{p.image_url && <img src={p.image_url} alt="" className="h-9 w-9 rounded object-cover" />}<span className="font-medium">{p.name}</span></TableCell>
                <TableCell className="text-xs">{p.sku}</TableCell>
                <TableCell>{p.category}</TableCell>
                <TableCell>{peso(p.price)}</TableCell>
                <TableCell><Badge variant={p.stock_status === "out" ? "destructive" : "secondary"}>{p.stock_status}</Badge></TableCell>
                <TableCell>{p.available ? "Yes" : "No"}</TableCell>
                <TableCell className="flex gap-2"><Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`}>Edit</Button><Button size="sm" variant="ghost" className="text-[#C62828]" onClick={() => archive(p)} data-testid={`archive-product-${p.id}`}>Archive</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit" : "Add"} Product</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="p-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Price</Label><Input type="number" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} data-testid="p-price" /></div>
                <div><Label>Category</Label>
                  <Select value={editing.category} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                    <SelectTrigger data-testid="p-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Description</Label><Textarea value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></div>
              <div><Label>Stock Status</Label>
                <Select value={editing.stock_status} onValueChange={(v) => setEditing({ ...editing, stock_status: v })}>
                  <SelectTrigger data-testid="p-stock"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="available">Available</SelectItem><SelectItem value="low">Low Stock</SelectItem><SelectItem value="out">Out of Stock</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2"><Switch checked={editing.available} onCheckedChange={(v) => setEditing({ ...editing, available: v })} data-testid="p-available" /> Available</label>
                <label className="flex items-center gap-2"><Switch checked={editing.featured} onCheckedChange={(v) => setEditing({ ...editing, featured: v })} /> Featured</label>
              </div>
              <div>
                <Label>Image</Label>
                <div className="flex items-center gap-3 mt-1">
                  {editing.image_url && <img src={editing.image_url} alt="" className="h-16 w-16 rounded-lg object-cover" />}
                  <label className="cursor-pointer inline-flex items-center gap-2 border rounded-lg px-3 py-2 text-sm hover:bg-muted" data-testid="p-upload">
                    <Upload size={16} /> {uploading ? "Uploading…" : "Upload"}
                    <input type="file" accept="image/*" className="hidden" onChange={upload} />
                  </label>
                </div>
              </div>
            </div>
          )}
          <DialogFooter><Button className="bg-[#1B5E20]" onClick={save} data-testid="save-product-btn">Save Product</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Branches ---------------- */
function Branches() {
  const [branches, setBranches] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => api.get("/branches").then((r) => setBranches(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const openNew = () => { setEditing({ code: "", name: "", subdivision: "", barangay: "", city: "", province: "Cavite", delivery_fee: 30, min_order: 0, delivery_enabled: true, active: true }); setOpen(true); };
  const save = async () => {
    try {
      if (editing.id) await api.put(`/branches/${editing.id}`, editing);
      else await api.post("/branches", { ...editing, delivery_fee: Number(editing.delivery_fee), min_order: Number(editing.min_order) });
      toast.success("Branch saved."); setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div>
      <div className="flex justify-between mb-4"><h3 className="font-semibold">Branches</h3><Button className="bg-[#1B5E20]" onClick={openNew} data-testid="add-branch-btn"><Plus size={16} className="mr-1" /> Add Branch</Button></div>
      <div className="grid md:grid-cols-2 gap-4">
        {branches.map((b) => (
          <div key={b.id} className="bg-white rounded-2xl border p-5" data-testid={`branch-${b.code}`}>
            <div className="flex justify-between"><div><Badge className="bg-[#1B5E20]">{b.code}</Badge><h4 className="font-display font-bold text-lg mt-2">{b.name}</h4></div><Badge variant={b.active ? "secondary" : "destructive"}>{b.active ? "Active" : "Disabled"}</Badge></div>
            <p className="text-sm text-muted-foreground mt-1">{b.subdivision}, Brgy. {b.barangay}, {b.city}, {b.province}</p>
            <div className="flex gap-4 mt-3 text-sm"><span>Delivery: <b>{peso(b.delivery_fee)}</b></span><span>Min: <b>{peso(b.min_order)}</b></span><span>{b.delivery_enabled ? "Delivery ON" : "Delivery OFF"}</span></div>
            <Button size="sm" variant="outline" className="mt-3" onClick={() => { setEditing({ ...b }); setOpen(true); }} data-testid={`edit-branch-${b.code}`}>Manage</Button>
          </div>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="branch-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Manage" : "Add"} Branch</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3"><div><Label>Code</Label><Input value={editing.code} onChange={(e) => setEditing({ ...editing, code: e.target.value.toUpperCase() })} data-testid="b-code" /></div><div><Label>Name</Label><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="b-name" /></div></div>
              <div className="grid grid-cols-2 gap-3"><div><Label>Subdivision</Label><Input value={editing.subdivision} onChange={(e) => setEditing({ ...editing, subdivision: e.target.value })} /></div><div><Label>Barangay</Label><Input value={editing.barangay} onChange={(e) => setEditing({ ...editing, barangay: e.target.value })} /></div></div>
              <div className="grid grid-cols-2 gap-3"><div><Label>City</Label><Input value={editing.city} onChange={(e) => setEditing({ ...editing, city: e.target.value })} /></div><div><Label>Province</Label><Input value={editing.province} onChange={(e) => setEditing({ ...editing, province: e.target.value })} /></div></div>
              <div className="grid grid-cols-2 gap-3"><div><Label>Delivery Fee</Label><Input type="number" value={editing.delivery_fee} onChange={(e) => setEditing({ ...editing, delivery_fee: e.target.value })} data-testid="b-fee" /></div><div><Label>Min Order</Label><Input type="number" value={editing.min_order} onChange={(e) => setEditing({ ...editing, min_order: e.target.value })} /></div></div>
              <div className="flex items-center gap-6"><label className="flex items-center gap-2"><Switch checked={editing.delivery_enabled} onCheckedChange={(v) => setEditing({ ...editing, delivery_enabled: v })} /> Delivery Enabled</label><label className="flex items-center gap-2"><Switch checked={editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} /> Active</label></div>
            </div>
          )}
          <DialogFooter><Button className="bg-[#1B5E20]" onClick={save} data-testid="save-branch-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Delivery & Payments ---------------- */
function DeliveryPayments() {
  const [methods, setMethods] = useState([]);
  useEffect(() => { api.get("/settings/payment-methods").then((r) => setMethods(r.data)); }, []);
  const toggle = async (code) => {
    const upd = methods.map((m) => m.code === code ? { ...m, active: !m.active } : m);
    setMethods(upd);
    await api.put("/settings/payment-methods", { methods: upd });
    toast.success("Payment methods updated.");
  };
  return (
    <div className="space-y-5">
      <div className="bg-white rounded-2xl border p-5">
        <h3 className="font-semibold mb-1">Payment Methods</h3>
        <p className="text-sm text-muted-foreground mb-4">Activate or deactivate payment options for customers.</p>
        {methods.map((m) => (
          <div key={m.code} className="flex items-center justify-between py-2 border-b last:border-0" data-testid={`pm-${m.code}`}>
            <span className="font-medium">{m.name}</span>
            <Switch checked={m.active} onCheckedChange={() => toggle(m.code)} data-testid={`pm-toggle-${m.code}`} />
          </div>
        ))}
      </div>
      <div className="bg-white rounded-2xl border p-5 text-sm text-muted-foreground">
        Delivery fees & zones are managed per-branch under <b>Branches</b>. Each branch supports its own fee, minimum order, and delivery on/off — ready for multi-branch expansion.
      </div>
    </div>
  );
}

/* ---------------- Customers ---------------- */
function Customers() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const load = (s = "") => api.get("/admin/customers", { params: s ? { search: s } : {} }).then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  return (
    <div>
      <div className="flex gap-2 mb-4 max-w-md"><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, mobile, email" data-testid="customer-search" /><Button className="bg-[#1B5E20]" onClick={() => load(q)} data-testid="customer-search-btn"><Search size={16} /></Button></div>
      <div className="bg-white rounded-2xl border overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Mobile</TableHead><TableHead>Orders</TableHead><TableHead>Total Value</TableHead><TableHead>Last Order</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {list.map((c) => (
              <TableRow key={c.id} data-testid={`customer-row-${c.id}`}>
                <TableCell className="font-medium">{c.name}</TableCell><TableCell>{c.phone}</TableCell>
                <TableCell>{c.order_count}</TableCell><TableCell>{peso(c.total_value)}</TableCell>
                <TableCell className="text-xs">{c.last_order ? new Date(c.last_order).toLocaleDateString() : "—"}</TableCell>
                <TableCell><Button size="sm" variant="outline" onClick={() => setSel(c)} data-testid={`view-customer-${c.id}`}>View</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent data-testid="customer-detail">
          <DialogHeader><DialogTitle>{sel?.name}</DialogTitle></DialogHeader>
          {sel && (
            <div className="text-sm space-y-2">
              <div><b>Email:</b> {sel.email}</div><div><b>Mobile:</b> {sel.phone}</div>
              <div><b>Address:</b> {sel.address ? fmtAddress(sel.address) : "—"}</div>
              <div><b>Registered:</b> {new Date(sel.registered).toLocaleDateString()}</div>
              <div><b>Total Orders:</b> {sel.order_count}</div>
              <div><b>Total Value:</b> {peso(sel.total_value)}</div>
              <div><b>Favorite:</b> {sel.favorite || "—"}</div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Staff ---------------- */
function Staff() {
  const [list, setList] = useState([]);
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", email: "", password: "", role: "store", branch_id: "" });
  const load = useCallback(() => api.get("/admin/staff").then((r) => setList(r.data)), []);
  useEffect(() => { load(); api.get("/branches").then((r) => setBranches(r.data)); }, [load]);
  const create = async () => {
    try { await api.post("/admin/staff", f); toast.success("Staff created."); setOpen(false); setF({ name: "", email: "", password: "", role: "store", branch_id: "" }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggleDisable = async (u) => { await api.put(`/admin/staff/${u.id}`, { disabled: !u.disabled }); load(); };
  return (
    <div>
      <div className="flex justify-between mb-4"><h3 className="font-semibold">Staff & Roles</h3><Button className="bg-[#1B5E20]" onClick={() => setOpen(true)} data-testid="add-staff-btn"><Plus size={16} className="mr-1" /> Add Staff</Button></div>
      <div className="bg-white rounded-2xl border overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>{list.map((u) => (
            <TableRow key={u.id} data-testid={`staff-row-${u.id}`}>
              <TableCell className="font-medium">{u.name}</TableCell><TableCell>{u.email}</TableCell>
              <TableCell><Badge>{u.role}</Badge></TableCell><TableCell>{u.disabled ? "Disabled" : "Active"}</TableCell>
              <TableCell>{u.role !== "superadmin" && <Button size="sm" variant="outline" onClick={() => toggleDisable(u)} data-testid={`toggle-staff-${u.id}`}>{u.disabled ? "Enable" : "Disable"}</Button>}</TableCell>
            </TableRow>
          ))}</TableBody>
        </Table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="staff-dialog">
          <DialogHeader><DialogTitle>Add Staff Account</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="s-name" /></div>
            <div><Label>Email</Label><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="s-email" /></div>
            <div><Label>Password</Label><Input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="s-password" /></div>
            <div><Label>Role</Label>
              <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
                <SelectTrigger data-testid="s-role"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="store">Store</SelectItem><SelectItem value="superadmin">Superadmin</SelectItem></SelectContent>
              </Select>
            </div>
            {f.role === "store" && <div><Label>Branch</Label>
              <Select value={f.branch_id} onValueChange={(v) => setF({ ...f, branch_id: v })}>
                <SelectTrigger data-testid="s-branch"><SelectValue placeholder="Select branch" /></SelectTrigger>
                <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select></div>}
          </div>
          <DialogFooter><Button className="bg-[#1B5E20]" onClick={create} data-testid="save-staff-btn">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Reports ---------------- */
function Reports() {
  const [period, setPeriod] = useState("daily");
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/admin/reports", { params: { period } }).then((r) => setData(r.data)), [period]);
  useEffect(() => { load(); }, [load]);
  const exportCsv = async () => {
    const res = await api.get("/admin/reports/export", { params: { period }, responseType: "blob" });
    const url = URL.createObjectURL(res.data); const a = document.createElement("a");
    a.href = url; a.download = `angelsabaw_report_${period}.csv`; a.click(); URL.revokeObjectURL(url);
  };
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        {["daily", "weekly", "monthly"].map((p) => <Button key={p} variant={period === p ? "default" : "outline"} className={period === p ? "bg-[#1B5E20]" : ""} onClick={() => setPeriod(p)} data-testid={`report-${p}`}>{p[0].toUpperCase() + p.slice(1)}</Button>)}
        <Button variant="outline" className="ml-auto" onClick={exportCsv} data-testid="export-report-btn"><Download size={16} className="mr-1" /> Export CSV</Button>
      </div>
      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat label="Gross Sales" value={peso(data.gross_sales)} accent="#1B5E20" />
            <Stat label="Net Sales" value={peso(data.net_sales)} />
            <Stat label="Orders" value={data.order_count} accent="#0284C7" />
            <Stat label="Avg Order" value={peso(data.avg_order_value)} accent="#B78103" />
            <Stat label="Delivery Revenue" value={peso(data.delivery_revenue)} />
            <Stat label="Cancelled" value={data.cancelled} accent="#EF4444" />
            <Stat label="Refunds" value={data.refunds} />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl border p-5"><h3 className="font-semibold mb-3">Sales by Category</h3>{Object.entries(data.by_category).map(([k, v]) => <div key={k} className="flex justify-between py-1 text-sm border-b last:border-0"><span>{k}</span><span>{peso(v)}</span></div>)}{Object.keys(data.by_category).length === 0 && <p className="text-sm text-muted-foreground">No data.</p>}</div>
            <div className="bg-white rounded-2xl border p-5"><h3 className="font-semibold mb-3">Top Products</h3>{data.top_products.map((p, i) => <div key={i} className="flex justify-between py-1 text-sm border-b last:border-0"><span>{p.name}</span><span>{peso(p.sales)}</span></div>)}{data.top_products.length === 0 && <p className="text-sm text-muted-foreground">No data.</p>}</div>
          </div>
        </>
      )}
    </div>
  );
}

/* ---------------- Audit ---------------- */
function Audit() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get("/admin/audit").then((r) => setLogs(r.data)); }, []);
  return (
    <div className="bg-white rounded-2xl border overflow-x-auto">
      <Table>
        <TableHeader><TableRow><TableHead>Time</TableHead><TableHead>User</TableHead><TableHead>Action</TableHead><TableHead>Entity</TableHead><TableHead>Change</TableHead></TableRow></TableHeader>
        <TableBody>{logs.map((l) => (
          <TableRow key={l.id} data-testid={`audit-row-${l.id}`}>
            <TableCell className="text-xs">{new Date(l.timestamp).toLocaleString()}</TableCell>
            <TableCell>{l.user_name} <Badge variant="secondary" className="text-[10px]">{l.user_role}</Badge></TableCell>
            <TableCell>{l.action}</TableCell><TableCell>{l.entity}</TableCell>
            <TableCell className="text-xs max-w-xs truncate">{l.new_value ? JSON.stringify(l.new_value) : "—"}{l.reason ? ` · ${l.reason}` : ""}</TableCell>
          </TableRow>
        ))}</TableBody>
      </Table>
      {logs.length === 0 && <p className="text-center py-10 text-muted-foreground">No audit entries yet.</p>}
    </div>
  );
}

/* ---------------- Health ---------------- */
function Health() {
  const [h, setH] = useState(null);
  useEffect(() => { const l = () => api.get("/admin/health").then((r) => setH(r.data)); l(); const t = setInterval(l, 10000); return () => clearInterval(t); }, []);
  if (!h) return <p className="text-muted-foreground">Loading…</p>;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="System Status" value={h.system_status.toUpperCase()} accent="#16A34A" />
        <Stat label="Database" value={h.database_status.toUpperCase()} accent={h.database_status === "connected" ? "#16A34A" : "#EF4444"} />
        <Stat label="Failed Txns" value={h.failed_transactions} accent="#EF4444" />
        <Stat label="Sync" value={h.sync_status.toUpperCase()} />
      </div>
      <div className="bg-white rounded-2xl border p-5">
        <h3 className="font-semibold mb-2">Last Successful Transaction</h3>
        <p className="text-sm">{h.last_transaction ? `${h.last_transaction.order_number} · ${new Date(h.last_transaction.created_at).toLocaleString()}` : "None yet"}</p>
      </div>
      <div className="bg-white rounded-2xl border p-5">
        <h3 className="font-semibold mb-3">Recent Errors ({h.error_count})</h3>
        {h.recent_errors.length === 0 ? <p className="text-sm text-muted-foreground">No errors logged. 🎉</p> :
          h.recent_errors.map((e, i) => <div key={i} className="text-sm border-b last:border-0 py-2" data-testid={`error-row-${i}`}><span className="text-[#C62828] font-medium">[{e.module}]</span> {e.message} <span className="text-xs text-muted-foreground">· {new Date(e.timestamp).toLocaleString()}</span></div>)}
      </div>
    </div>
  );
}

const SECTIONS = { overview: Overview, orders: Orders, products: Products, branches: Branches, delivery: DeliveryPayments, customers: Customers, staff: Staff, reports: Reports, audit: Audit, health: Health };

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [mobileNav, setMobileNav] = useState(false);
  const Section = SECTIONS[tab];
  const label = NAV.find((n) => n.key === tab)?.label;

  return (
    <div className="min-h-screen bg-[#F4F6F4] flex">
      <aside className={`${mobileNav ? "block" : "hidden"} md:block fixed md:static z-40 inset-y-0 left-0 w-64 bg-[#0E3A14] text-white flex-col`}>
        <div className="p-5 border-b border-white/10"><BrandLogo size="sm" light /></div>
        <nav className="p-3 space-y-1 overflow-y-auto">
          {NAV.map((n) => (
            <button key={n.key} onClick={() => { setTab(n.key); setMobileNav(false); }} data-testid={`nav-${n.key}`}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${tab === n.key ? "bg-[#FFB300] text-[#1A251C]" : "text-white/70 hover:bg-white/10"}`}>
              <n.icon size={18} /> {n.label}
            </button>
          ))}
          <button onClick={async () => { await logout(); nav("/"); }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-white/70 hover:bg-white/10 mt-4" data-testid="admin-logout"><LogOut size={18} /> Log out</button>
        </nav>
      </aside>

      <div className="flex-1 min-w-0">
        <header className="bg-white border-b h-16 flex items-center justify-between px-4 sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button className="md:hidden" onClick={() => setMobileNav((v) => !v)} data-testid="mobile-nav-toggle"><Menu /></button>
            <h1 className="font-display text-xl font-bold">{label}</h1>
          </div>
          <Badge className="bg-[#1B5E20]">Superadmin · {user.name}</Badge>
        </header>
        <div className="p-4 md:p-6"><Section /></div>
      </div>
    </div>
  );
}
